import { PublicKey } from '@solana/web3.js';
import type { PrintItem } from '@whaleden/core/types/ComicBook';

export type IOrderDepositPda = {
    key: string;
    data: IOrderDeposit;
};

export type IOrderDeposit = {
    owner: string;
    mint: string;
    seed: number;
    created: number;
    status: number;
    locked: boolean;
    user: string;
};

export class OrderDeposit {
    pda: string;
    owner: string;
    mint: string;
    created: number;
    status: number;
    locked: boolean;
    user: string;

    constructor({ user, mint, locked, created, status, owner }: IOrderDeposit, pda: string) {
        this.pda = pda;
        this.user = user;
        this.mint = mint;
        this.locked = locked;
        this.created = created;
        this.status = status;
        this.owner = owner;
    }
}

export enum OrderBlockchainState {
    CREATED = 0,
    ORDER_ASSIGNED = 1,
    COPY_PREPARED = 2,
}

export interface IGetDepositParams {
    owner?: PublicKey;
    mint?: PublicKey;
    locked?: boolean;
    status?: OrderBlockchainState;
    userId?: string;
}

export enum OrderFinancialStatus {
    // The payment provider has validated the customer's payment information.
    // This status appears only for manual payment capture and indicates payments should be captured before
    // the authorization period expires.
    AUTHORIZED = 'AUTHORIZED',

    // Payment was automatically or manually captured, or the order was marked as paid.
    PAID = 'PAID',

    // A payment was manually captured for the order with an amount less than the full order value.
    PARTIALLY_PAID = 'PARTIALLY_PAID',

    // The amount refunded to a customer is less than the full amount paid for an order.
    PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',

    // Orders have this status when the payment provider needs time to complete the payment,
    // or when manual payment methods are being used.
    PENDING = 'PENDING',

    // The full amount paid for an order was refunded to the customer.
    REFUNDED = 'REFUNDED',

    // An unpaid (payment authorized but not captured) order was manually canceled.
    VOIDED = 'VOIDED',
}

export enum OrderFulfillmentStatus {
    // All the items in the order have been fulfilled.
    FULFILLED = 'FULFILLED',

    // Some of the items in the order have been fulfilled,
    // or a request for fulfillment has been sent to the fulfillment service.
    IN_PROGRESS = 'IN_PROGRESS',

    // All of the unfulfilled items in this order are on hold.
    ON_HOLD = 'ON_HOLD',

    // None of the items in the order have been fulfilled.
    // Replaced by "UNFULFILLED" status.
    OPEN = 'OPEN',

    // Some of the items in the order have been fulfilled.
    PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',

    // A request for fulfillment of some items awaits a response from the fulfillment service.
    // Replaced by the "IN_PROGRESS" status.
    PENDING_FULFILLMENT = 'PENDING_FULFILLMENT',

    // Some of the items in the order have been rejected for fulfillment by the fulfillment service.
    REQUEST_DECLINED = 'REQUEST_DECLINED',

    // All the items in the order have been restocked.
    // Replaced by the "UNFULFILLED" status.
    RESTOCKED = 'RESTOCKED',

    // All of the unfulfilled items in this order are scheduled for fulfillment at later time.
    SCHEDULED = 'SCHEDULED',

    // None of the items in the order have been fulfilled.
    UNFULFILLED = 'UNFULFILLED',
}

export enum OrderState {
    UNPAID = 'UNPAID', // Waiting for finishing order and payment
    UNFULFILLED = 'UNFULFILLED', // Waiting for the order to be shipped
    FULFILLED = 'FULFILLED', // Order was shipped
    DELIVERED = 'DELIVERED', // Order was delivered
}

type ShopifyAmount = {
    amount: string;
    currencyCode: string;
};

type ShopifyOrderFulfillment = {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    totalQuantity: number;
    estimatedDeliveryAt: string | null;
    deliveredAt: string | null;
    status: ShopifyOrderFulfillmentStatus;
    trackingInfo: ShopifyOrderTrackingInfo[];
};

type ShopifyOrderTrackingInfo = {
    company: string;
    number: string;
    url: string;
};

enum ShopifyOrderFulfillmentStatus {
    CANCELLED = 'CANCELLED',
    // The fulfillment was canceled.

    ERROR = 'ERROR',
    // There was an error with the fulfillment request.

    FAILURE = 'FAILURE',
    // The fulfillment request failed.

    SUCCESS = 'SUCCESS',
    // The fulfillment was completed successfully.

    OPEN = 'OPEN',
    // The third-party fulfillment service has acknowledged the fulfillment and is processing it.

    PENDING = 'PENDING',
    // Shopify has created the fulfillment and is waiting for the third-party fulfillment service to transition it to open or success.
}

type ShopifyOrderPriceSet = {
    presentmentMoney: ShopifyAmount;
    shopMoney: ShopifyAmount;
};

type ShippingAddress = {
    line1: string;
    line2?: string | null;
    city: string;
    zipCode: string;
    state: string;
    country: string;
};

export interface IOrder {
    id: string;
    orderNumber: number;
    userId: string;

    name: string | null;
    email: string | null;
    surname: string | null;
    phone: string | null;
    shippingAddress: ShippingAddress | null;

    fulfillments: ShopifyOrderFulfillment[] | null;
    totalPriceSet: ShopifyOrderPriceSet | null;

    items: (PrintItem & { deposit: string })[]; // persistent deposited item info & PDAs of deposited nfts

    shopifyOrderId: string | null;
    shopifyFinancialStatus: OrderFinancialStatus | null;
    shopifyFulfillmentStatus: OrderFulfillmentStatus | null;
}

export class Order {
    id: string;
    orderNumber: number;
    userId: string;

    name: string | null;
    email: string | null;
    surname: string | null;
    phone: string | null;
    shippingAddress: ShippingAddress | null;

    fulfillments: ShopifyOrderFulfillment[] | null;
    totalPriceSet: ShopifyOrderPriceSet | null;

    items: (PrintItem & { deposit: string })[]; // persistent deposited item info & PDAs of deposited nfts

    shopifyOrderId: string | null;
    shopifyFinancialStatus: OrderFinancialStatus | null;
    shopifyFulfillmentStatus: OrderFulfillmentStatus | null;

    constructor({
        id,
        name,
        fulfillments,
        shopifyFulfillmentStatus,
        shopifyFinancialStatus,
        shopifyOrderId,
        totalPriceSet,
        orderNumber,
        items,
        phone,
        shippingAddress,
        surname,
        userId,
        email,
    }: IOrder) {
        this.id = id;
        this.name = name;
        this.orderNumber = orderNumber;
        this.items = items;
        this.phone = phone;
        this.shippingAddress = shippingAddress;
        this.surname = surname;
        this.userId = userId;
        this.fulfillments = fulfillments;
        this.email = email;
        this.shopifyFulfillmentStatus = shopifyFulfillmentStatus;
        this.shopifyFinancialStatus = shopifyFinancialStatus;
        this.shopifyOrderId = shopifyOrderId;
        this.totalPriceSet = totalPriceSet;
    }

    get state() {
        if (this.shopifyFinancialStatus === null || this.shopifyFulfillmentStatus === null) {
            return OrderState.UNPAID;
        }

        switch (this.shopifyFinancialStatus) {
            case OrderFinancialStatus.AUTHORIZED:
            case OrderFinancialStatus.PENDING:
            case OrderFinancialStatus.PARTIALLY_PAID:
            case OrderFinancialStatus.VOIDED:
            case OrderFinancialStatus.REFUNDED:
            case OrderFinancialStatus.PARTIALLY_REFUNDED:
                return OrderState.UNPAID;

            case OrderFinancialStatus.PAID:
                switch (this.shopifyFulfillmentStatus) {
                    case OrderFulfillmentStatus.FULFILLED:
                        return OrderState.FULFILLED;

                    case OrderFulfillmentStatus.IN_PROGRESS:
                    case OrderFulfillmentStatus.ON_HOLD:
                    case OrderFulfillmentStatus.OPEN:
                    case OrderFulfillmentStatus.PARTIALLY_FULFILLED:
                    case OrderFulfillmentStatus.PENDING_FULFILLMENT:
                    case OrderFulfillmentStatus.REQUEST_DECLINED:
                    case OrderFulfillmentStatus.RESTOCKED:
                    case OrderFulfillmentStatus.SCHEDULED:
                    case OrderFulfillmentStatus.UNFULFILLED:
                        return OrderState.UNFULFILLED;
                }
        }
    }
}

export const OrderStateDesc: Record<OrderState, string> = {
    [OrderState.UNPAID]: 'Waiting for finishing payment',
    [OrderState.UNFULFILLED]: 'Waiting for the order to be shipped',
    [OrderState.FULFILLED]: 'Order has been shipped',
    [OrderState.DELIVERED]: 'Your order has been delivered',
};

export const OrderBlockchainStateLabels: Record<OrderBlockchainState, string> = {
    [OrderBlockchainState.CREATED]: 'Withdraw available',
    [OrderBlockchainState.ORDER_ASSIGNED]: 'Locked in order',
    [OrderBlockchainState.COPY_PREPARED]: 'Locked for shipping',
};
